import { useArchives, usePatient } from '@hook'
import { DownloadIcon, PencilIcon } from '@icons'
import { ADMIN_MODAL_TYPE, useModalStore } from '@modal'
import type { ArchiveDocumentFile, ArchiveDocumentIncluded, ArchiveDocumentResource } from '@type/archive.types'
import {
  getArchiveDocumentCreatedByName,
  getArchiveDocumentFolderNumber,
  getArchiveFacilityName,
  getArchivePatientName
} from '@type/archive.types'
import { SideDrawer } from '@ui'
import { showToast } from '@util'
import moment from 'moment'
import type { ReactNode } from 'react'

type Props = {
  onClose: () => void
  document?: ArchiveDocumentResource
  included?: ArchiveDocumentIncluded[]
}

const formatLongDate = (value?: string) => (value ? moment(value).format('D MMMM, YYYY') : '—')

const formatTypeLabel = (value?: string) =>
  value ? value.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()) : '—'

const formatContextLabel = (value?: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '—')

const DetailField = ({ label, value }: { label: string; value: ReactNode }) => (
  <div>
    <p className="text-xs text-[#9CA3AF]">{label}</p>
    <p className="mt-0.5 text-sm font-semibold text-[#111827]">{value || '—'}</p>
  </div>
)

const openFileUrl = (url?: string) => {
  if (!url) {
    showToast('File URL is unavailable', { type: 'error' })
    return
  }
  window.open(url, '_blank', 'noopener,noreferrer')
}

const downloadFile = async (file: ArchiveDocumentFile) => {
  try {
    const response = await fetch(file.url)
    const blob = await response.blob()
    const objectUrl = URL.createObjectURL(blob)
    const link = window.document.createElement('a')
    link.href = objectUrl
    link.download = file.filename
    window.document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(objectUrl)
  } catch {
    openFileUrl(file.url)
  }
}

const ScannedDocumentDetails = ({ onClose, document: doc, included = [] }: Props) => {
  const { openModal, closeModal } = useModalStore()
  const folderId = doc?.relationships.archive_folder?.data?.id
  const {
    archive,
    singleIncluded = [],
    isLoading: folderLoading
  } = useArchives({
    id: folderId,
    enabled: !!folderId
  })

  const patientId = archive?.relationships.patient?.data?.id
  const { singlePatient } = usePatient({ id: patientId })

  if (!doc) return null

  const attrs = doc.attributes
  const files = attrs.files ?? []
  const pageCount = files.length

  const patientNameFromFolder = archive ? getArchivePatientName(archive, singleIncluded) : '—'
  const patientName =
    [singlePatient?.attributes?.first_name, singlePatient?.attributes?.last_name].filter(Boolean).join(' ') ||
    (patientNameFromFolder !== '—' ? patientNameFromFolder : attrs.specialty) ||
    '—'

  const folderNumber = getArchiveDocumentFolderNumber(doc, included)
  const facilityName = archive ? getArchiveFacilityName(archive, singleIncluded) : ''
  const subtitle = [folderNumber, facilityName || attrs.specialty, 'Main Campus'].filter(Boolean).join(' · ')

  const uploadedBy = getArchiveDocumentCreatedByName(doc, included)
  const doctorRel = doc.relationships.doctor?.data
  const doctorFromIncluded =
    doctorRel && included.length ? included.find(item => item.type === 'staff' && item.id === doctorRel.id) : undefined
  const doctorName =
    doctorFromIncluded && doctorFromIncluded.type === 'staff'
      ? [doctorFromIncluded.attributes.first_name, doctorFromIncluded.attributes.last_name].filter(Boolean).join(' ')
      : attrs.specialty || '—'

  const handleDownload = async () => {
    if (!files.length) {
      showToast('No files available to download', { type: 'error' })
      return
    }
    for (const file of files) {
      await downloadFile(file)
    }
  }

  const handleEditMetadata = () => {
    openModal(ADMIN_MODAL_TYPE.UPLOAD_ARCHIVE_DOCUMENT, {
      folder: archive,
      included: singleIncluded,
      document: doc
    })
  }

  const handleOpenFileRecord = () => {
    if (!archive) {
      showToast(folderLoading ? 'Loading file record…' : 'Physical file record not found', {
        type: 'error'
      })
      return
    }
    closeModal(ADMIN_MODAL_TYPE.ARCHIVE_DOCUMENT_DETAILS)
    openModal(ADMIN_MODAL_TYPE.ARCHIVE_FILE_DETAILS, { folder: archive, included: singleIncluded })
  }

  return (
    <SideDrawer onClose={onClose} title="File Details">
      <div className="flex h-full flex-col">
        <div className="flex-1 overflow-y-auto px-8 py-4">
          <h3 className="text-xl font-semibold text-[#111827]">{patientName}</h3>
          <p className="mt-1 text-sm text-[#6B7280]">{subtitle}</p>

          <div className="mt-4 rounded-xl bg-[#F3F4F6] px-4 py-3">
            <p className="text-sm font-semibold text-[#111827]">{attrs.title}</p>
            <p className="mt-0.5 text-xs text-[#6B7280]">
              {pageCount} {pageCount === 1 ? 'page' : 'pages'}. Click a page to view
            </p>
          </div>

          <div className="mt-6">
            <h4 className="mb-3 text-xs font-semibold tracking-wide text-[#6B7280]">PAGES</h4>
            {files.length === 0 ? (
              <p className="text-sm text-[#6B7280]">No pages uploaded.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {files.map((file, index) => (
                  <div
                    className="flex items-center justify-between rounded-xl border border-[#E5E7EB] bg-white px-4 py-3"
                    key={file.id}
                  >
                    <p className="text-sm font-medium text-[#111827]">Page {index + 1}</p>
                    <button
                      className="rounded-full border border-[#D1D5DB] px-4 py-1.5 text-sm text-[#374151] hover:bg-[#F9FAFB]"
                      onClick={() => openFileUrl(file.url)}
                      type="button"
                    >
                      Open
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-6">
            <h4 className="mb-3 text-xs font-semibold tracking-wide text-[#6B7280]">DOCUMENT DETAILS</h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <DetailField label="Title" value={attrs.title} />
              <DetailField label="Type" value={formatTypeLabel(attrs.document_type)} />
              <DetailField label="Date of service" value={formatLongDate(attrs.document_date)} />
              <DetailField label="Provider" value={doctorName} />
              <DetailField label="Encounter" value={formatContextLabel(attrs.clinical_context)} />
              <DetailField label="Diagnosis" value={attrs.diagnosis} />
            </div>
          </div>

          <div className="mt-6">
            <h4 className="mb-3 text-xs font-semibold tracking-wide text-[#6B7280]">UPLOAD INFO</h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DetailField label="Uploaded By" value={uploadedBy} />
              <DetailField label="Uploaded On" value={formatLongDate(attrs.created_at || attrs.digitized_at)} />
            </div>
          </div>
        </div>

        <div className="mt-auto flex flex-wrap gap-2 border-t border-[#E5E7EB] px-8 py-4">
          <button
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#F3F4F6] px-4 py-3 text-sm font-medium text-[#374151] hover:bg-[#E5E7EB]"
            onClick={handleDownload}
            type="button"
          >
            <DownloadIcon />
            Download
          </button>
          <button
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#F3F4F6] px-4 py-3 text-sm font-medium text-[#374151] hover:bg-[#E5E7EB]"
            // onClick={handleEditMetadata}
            onClick={() => undefined}
            type="button"
          >
            <PencilIcon />
            Edit meta data
          </button>
          <button
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#F3F4F6] px-4 py-3 text-sm font-medium text-[#374151] hover:bg-[#E5E7EB]"
            onClick={handleOpenFileRecord}
            type="button"
          >
            <svg fill="none" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M2 13.333V4a1.333 1.333 0 0 1 1.333-1.333h3.334L8 4h4.667A1.333 1.333 0 0 1 14 5.333v8a1.333 1.333 0 0 1-1.333 1.334H3.333A1.333 1.333 0 0 1 2 13.333Z"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
              />
            </svg>
            Open file record
          </button>
        </div>
      </div>
    </SideDrawer>
  )
}

export default ScannedDocumentDetails
